import React, { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { BuildingOffice2Icon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';

interface TenantSelectionGuardProps {
  children: React.ReactNode;
}

/**
 * SECURITY: Guards the application to ensure a tenant is selected before allowing access.
 *
 * Security Principles:
 * 1. Regular users should NEVER see a tenant selection - they get auto-routed to their tenant
 * 2. Only SuperAdmin users see the tenant selection modal
 * 3. If a regular user doesn't have a tenant, they see an error (not a selection)
 */
export const TenantSelectionGuard: React.FC<TenantSelectionGuardProps> = ({ children }) => {
  const { currentTenant, availableTenants, switchTenant, isLoading, isSuperAdmin, refreshTenants } =
    useTenant();
  const { logout, isAuthenticated } = useAuth();
  const [waitingForTenant, setWaitingForTenant] = useState(true);
  const [checkCount, setCheckCount] = useState(0);

  // CRITICAL: After authentication, wait for tenant context to sync
  // This handles the race condition where AuthContext stores tenant AFTER TenantContext initializes
  useEffect(() => {
    if (currentTenant) {
      // Tenant found, stop waiting
      setWaitingForTenant(false);
      return;
    }

    if (!isAuthenticated) {
      // Not authenticated yet, keep waiting
      return;
    }

    // Authenticated but no tenant - poll for it
    const checkForTenant = async () => {
      // Check localStorage directly for tenant (in case TenantContext hasn't picked it up yet)
      const storedTenant = localStorage.getItem('mdm_tenant');
      if (storedTenant) {
        try {
          const tenant = JSON.parse(storedTenant);
          if (tenant && tenant.tenantId) {
            console.log(
              'TenantSelectionGuard: Found tenant in localStorage, refreshing context...'
            );
            await refreshTenants();
            return;
          }
        } catch (e) {
          console.error('Failed to parse tenant:', e);
        }
      }

      // Increment check count
      setCheckCount(prev => prev + 1);
    };

    // Poll every 100ms for up to 5 seconds (50 checks)
    const interval = setInterval(() => {
      if (checkCount < 50) {
        checkForTenant();
      } else {
        // Give up after 5 seconds
        clearInterval(interval);
        setWaitingForTenant(false);
      }
    }, 100);

    // Also check immediately
    checkForTenant();

    return () => clearInterval(interval);
  }, [isAuthenticated, currentTenant, checkCount, refreshTenants]);

  // Show loading state while initializing OR while waiting for tenant after login
  if (isLoading || (isAuthenticated && waitingForTenant && !currentTenant)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">{isLoading ? 'Loading...' : 'Initializing tenant...'}</p>
        </div>
      </div>
    );
  }

  // If no tenant selected
  if (!currentTenant) {
    // SECURITY: Only SuperAdmin can see tenant selection
    if (isSuperAdmin && availableTenants.length > 0) {
      return (
        <Dialog.Root open={true}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
            <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl p-8 max-w-md w-full z-50">
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <BuildingOffice2Icon className="w-10 h-10 text-blue-600" />
                </div>
                <Dialog.Title className="text-2xl font-bold text-gray-900 mb-2">
                  Select a Tenant
                </Dialog.Title>
                <Dialog.Description className="text-gray-600">
                  As a SuperAdmin, choose which tenant's data to manage.
                </Dialog.Description>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {availableTenants.map(tenant => (
                  <button
                    key={tenant.tenantId}
                    onClick={() => switchTenant(tenant.tenantId)}
                    className="w-full text-left px-4 py-4 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <BuildingOffice2Icon className="w-6 h-6 text-gray-600 group-hover:text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">
                          {tenant.tenantName}
                        </div>
                        <div className="text-sm text-gray-500 truncate">{tenant.tenantCode}</div>
                        {tenant.description && (
                          <div className="text-xs text-gray-400 truncate mt-1">
                            {tenant.description}
                          </div>
                        )}
                      </div>
                      <div className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        &rarr;
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  SuperAdmin: You can switch tenants using the switcher in the header.
                </p>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      );
    }

    // SECURITY: Regular users without a tenant see an error message
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ExclamationTriangleIcon className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Tenant Access</h1>
          <p className="text-gray-600 mb-6">
            Your account is not associated with any tenant. Please contact your administrator to get
            access to your organization's data.
          </p>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              If you believe this is an error, try logging out and logging back in.
            </p>
            <button
              onClick={logout}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tenant is selected, render the application
  return <>{children}</>;
};
